import { Scroll } from "phosphor-react";
import {
    getActiveDeclarationType,
    type MandateDetails,
} from "../../../models/api/admin/mandateDetails";
import { DeclarationType } from "../../../models/app/eDeclarations";
import { convertDateStringToReadableString } from "../../../utils/convertDateStringToReadableString";
import ActionableStatus from "../../ActionableStatus";
import ActionableStatusTag from "../../ActionableStatus/ActionableStatusTag";
import useDeclarationStatusActions from "./useDeclarationStatusActions";
import { ApiDriverByIdResponse } from "../../../models/api/admin/driver";
import { isUkLicence } from "../../../constants/countries";

export type DeclarationStatusPendingProps = {
    mandateDetails: MandateDetails;
    driverData?: ApiDriverByIdResponse;
    isS2DUser: boolean;
    handleScrollToDocumentUpload: () => void;
};

const DeclarationStatusPending: React.FC<DeclarationStatusPendingProps> = ({
    mandateDetails,
    driverData,
    isS2DUser,
    handleScrollToDocumentUpload,
}) => {
    const {
        declarationId,
        declarationRequested,
        licenceCountryOrigin,    
        thirdReminderSentDate,
        declarationReminderLastSentDate,
    } = mandateDetails;
    const activeDeclarationType = getActiveDeclarationType(mandateDetails);

    const {
        resendEDeclarationAction,
        resendPaperDeclarationAction,
        switchToEDeclarationAction,
        switchToPaperDeclarationAction,
        uploadSignedDeclarationAction,
    } = useDeclarationStatusActions(
        declarationId,
        mandateDetails,
        false,
        handleScrollToDocumentUpload,
        driverData
    );

    const statusIndicators = new Array(0);
    if (activeDeclarationType === "PaperDeclaration") {
        statusIndicators.push(
            <ActionableStatusTag theme="white" text="Paper" />
        );
    }
    //status tag will either be pending or incomplete no matter the status
    if (thirdReminderSentDate) {
        statusIndicators.push(<ActionableStatusTag theme="incomplete" />);
    } else {
        statusIndicators.push(<ActionableStatusTag theme="pending" />);
    }

    const sendStatusActions = () => {
        if (activeDeclarationType === DeclarationType.EDeclaration) {
            return [resendEDeclarationAction, switchToPaperDeclarationAction];
        }

        const paperActions = isS2DUser
            ? [uploadSignedDeclarationAction, resendPaperDeclarationAction]
            : [resendPaperDeclarationAction];

        return isUkLicence(licenceCountryOrigin)
            ? [...paperActions, switchToEDeclarationAction]
            : paperActions;
    };

    const StatusContent = () => {
        if (activeDeclarationType === DeclarationType.EDeclaration) {
            return (
                <div className="flex flex-col text-sm">
                    <span>
                        {`E-Declaration request sent: ${convertDateStringToReadableString(declarationRequested)}`}
                    </span>
                    {declarationReminderLastSentDate && (
                        <span>
                            {`E-Declaration reminder last sent: ${convertDateStringToReadableString(declarationReminderLastSentDate)}`}
                        </span>
                    )}
                </div>
            );
        } else {
            return (
                <div className="flex flex-col text-sm">
                    <span>
                        {`Paper Declaration Requested: ${convertDateStringToReadableString(declarationRequested)}`}
                    </span>
                    {declarationReminderLastSentDate ? (
                        <span>
                            {`Paper Declaration reminder last sent: ${convertDateStringToReadableString(declarationReminderLastSentDate)}`}
                        </span>
                    ) : (
                        <span>
                            {"No signed declaration uploaded."}
                        </span>
                    )}
                </div>
            );
        }
    };

    return (
        <ActionableStatus
            title="Declaration status"
            theme="grey"
            actionable
            statusIndicators={statusIndicators}
            actions={sendStatusActions()}
            hasEmailAddress={!!driverData?.emailAddress}
            declarationComplete={false}
            licenceCountryOrigin={licenceCountryOrigin ?? undefined}
        >
            <div className="flex items-center">
                <div className="pr-3">
                    <Scroll size={24} weight="duotone" />
                </div>
                <StatusContent />
            </div>
        </ActionableStatus>
    );
};

export default DeclarationStatusPending;
